import { Icon, type IconName } from "@ui/components/Icon";
import { NAV_PREFETCH_CACHE_FOR } from "@ui/lib/prefetch-config";
import type { PrefetchProps } from "@ui/lib/prefetch-context";
import { tv } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";
import type { ReactNode } from "react";

export type BottomBarItemColor = "red" | "green" | "yellow" | "purple" | "blue" | "orange" | "pink" | "neutral";

const bottomBarItem = tv({
  defaultVariants: {
    color: "neutral",
    isActive: false,
  },
  slots: {
    root: [
      "group relative flex h-12 min-w-0 flex-1 basis-0 items-center justify-center rounded-2xl outline-none",
      "focus-visible:ring-2 focus-visible:ring-offset-1",
    ],
    fill: "absolute inset-y-0 -inset-x-1 rounded-2xl transition-colors",
    content: "relative z-10 flex w-full min-w-0 flex-col items-center justify-center gap-0.5",
    media: "relative flex size-6 items-center justify-center transition-colors",
    label: "max-w-full truncate text-center font-body text-xs font-medium leading-4 tracking-normal transition-colors",
  },
  variants: {
    color: {
      red: { root: "focus-visible:ring-red-400" },
      green: { root: "focus-visible:ring-green-500" },
      yellow: { root: "focus-visible:ring-yellow-500" },
      purple: { root: "focus-visible:ring-purple-500" },
      blue: { root: "focus-visible:ring-blue-500" },
      orange: { root: "focus-visible:ring-orange-500" },
      pink: { root: "focus-visible:ring-pink-500" },
      neutral: { root: "focus-visible:ring-neutral-500" },
    },
    isActive: {
      true: {},
      false: {
        media: "text-neutral-600",
        label: "text-neutral-600",
      },
    },
  },
  compoundVariants: [
    { color: "red", isActive: true, class: { fill: "bg-red-50", media: "text-red-700", label: "text-red-700" } },
    {
      color: "green",
      isActive: true,
      class: { fill: "bg-green-100", media: "text-green-700", label: "text-green-700" },
    },
    {
      color: "yellow",
      isActive: true,
      class: { fill: "bg-yellow-100", media: "text-yellow-700", label: "text-yellow-700" },
    },
    {
      color: "purple",
      isActive: true,
      class: { fill: "bg-purple-100", media: "text-purple-600", label: "text-purple-600" },
    },
    { color: "blue", isActive: true, class: { fill: "bg-blue-100", media: "text-blue-700", label: "text-blue-700" } },
    {
      color: "orange",
      isActive: true,
      class: { fill: "bg-orange-100", media: "text-orange-600", label: "text-orange-600" },
    },
    { color: "pink", isActive: true, class: { fill: "bg-pink-50", media: "text-pink-700", label: "text-pink-700" } },
    {
      color: "neutral",
      isActive: true,
      class: { fill: "bg-neutral-100", media: "text-neutral-700", label: "text-neutral-700" },
    },
  ],
});

export type BottomBarItemProps = {
  href: string;
  label: string;
  isActive?: boolean;
  color?: BottomBarItemColor;
  /** Shows a small red unread dot on the tab glyph when true. */
  badge?: boolean;
} & Pick<PrefetchProps, "prefetch" | "cacheFor"> &
  ({ icon: IconName; media?: never } | { icon?: never; media: ReactNode });

/**
 * A single navigation tab inside `BottomBar`. Pass `icon` for a built-in line glyph, or
 * `media` for a custom slot (e.g. an `Avatar` for the profile tab). Active state is driven
 * by the consumer via `isActive` so the bar can match `usePage().url` precisely.
 *
 * Layout: the item sits 48px tall inside a 56px bar (4px gap top/bottom). When active, the
 * filled background bleeds 4px on each side via `-inset-x-1`, exactly filling the inter-item
 * gap so the active pill reads as one contiguous shape (matches Figma node 1391:7917).
 *
 * Icons opt out of the sticker set's signature lean (`tilt="none"`): in a row of evenly
 * spaced tabs a per-icon rotation reads as misalignment rather than as the set's character.
 */
export function BottomBarItem({
  href,
  label,
  isActive = false,
  color = "neutral",
  badge = false,
  icon,
  media,
  prefetch = "mount",
  cacheFor = NAV_PREFETCH_CACHE_FOR,
}: BottomBarItemProps) {
  const styles = bottomBarItem({ color, isActive });

  return (
    <Link
      href={href}
      prefetch={prefetch}
      cacheFor={cacheFor}
      aria-current={isActive ? "page" : undefined}
      className={styles.root()}
    >
      {isActive ? <span aria-hidden="true" className={styles.fill()} /> : null}
      <span className={styles.content()}>
        <span aria-hidden="true" className={styles.media()}>
          {media ?? (icon ? <Icon name={icon} size="24" tilt="none" type={isActive ? "sticker" : "line"} /> : null)}
          {badge ? (
            <span className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-red-400 ring-2 ring-white" />
          ) : null}
        </span>
        <span className={styles.label()}>{label}</span>
      </span>
    </Link>
  );
}
